import { useMemo } from "react";
import {
  ApacHotelsComponent,
  useApacHotelsStyles,
} from "@apac/components/hotels";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { LodgingAddressEnum, type Lodging } from "@b2bportal/lodging-api";
import { Dialog, DialogContent, DialogTrigger, Icon } from "@tenant/ui";
import clsx from "clsx";
import { HotelShopMap } from "./HotelShopMap";
import defaultStyles from "./styles.module.scss";

export type LocationWithMapProps = PlatformComponentProps & {
  hotel: Lodging;
  clickable?: boolean;
  triggerClassName?: string;
  icon?: IconNameEnum;
  apiKey?: string;
};

export const LocationWithMap = ({
  hotel,
  clickable,
  triggerClassName,
  icon,
  apiKey = import.meta.env.VITE_REACT_APP_GOOGLE_MAPS_API_KEY,
}: LocationWithMapProps) => {
  const COMPONENT_KEY = ApacHotelsComponent.LocationWithMap;
  const styles = useApacHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const location = useMemo(() => {
    const address = hotel.lodging.address;
    const line =
      address.LodgingAddress === LodgingAddressEnum.Exact
        ? address.line1
        : address.city;
    return line.split(",")?.[0].trim();
  }, [hotel.lodging.address]);

  const LocationDisplay = ({ location }) => (
    <div className={clsx(cn("location"), triggerClassName)}>
      <Icon className={cn("icon")} iconName={icon ?? IconNameEnum.mapPin} />
      <span className={cn("city-text")}>{location}</span>
    </div>
  );

  return (
    <div className={block}>
      {clickable ? (
        <Dialog>
          <DialogTrigger>
            <LocationDisplay location={location} />
          </DialogTrigger>
          <DialogContent>
            <HotelShopMap hotel={hotel} icon={icon} apiKey={apiKey} />
          </DialogContent>
        </Dialog>
      ) : (
        <LocationDisplay location={location} />
      )}
    </div>
  );
};
